import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Save, ChevronDown, ChevronRight, Info, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { updateQaSettings } from '@/lib/edge-functions'

// One row of carlos_step_rubric — the per-(step_type, day_offset) policy Carlos
// reads when it scores a message. Only the fields Carlos actually consumes are
// editable here (threshold / min_acceptable / max_attempts / addendum).
export interface StepRubricRow {
  id: string
  org_id: string
  step_type: string
  day_offset: number
  threshold: number
  min_acceptable: number
  max_attempts: number
  system_prompt_addendum: string | null
  // Provenance of the EFFECTIVE row this card renders: `null` = inherited from
  // the org-global (cadence_id IS NULL) legacy row; a string = a row already
  // scoped to this cadence. `undefined` = caller didn't thread provenance (older
  // callers), in which case we skip the provenance badge. The runtime objects the
  // PolicySection merger passes are RawRubricRow, so this field arrives populated.
  cadence_id?: string | null
  // JSONB filters Carlos uses to narrow which risk_triggers apply for this
  // (step_type, day_offset). NOT edited in the UI — carried through the read/save
  // round-trip so that forking an inherited rubric (save creates a cadence-scoped
  // copy) does NOT drop them. Losing them makes the Day-0 invite / Day-11 DM
  // escalate instead of auto-sending. Pass-through only.
  rubric_focus?: unknown | null
  rubric_skip?: unknown | null
}

// ── Drift detection (prose addendum vs. effective column) ─────────────────────
// Criterio (documentado para futuros toques): el heurístico de keywords tenía
// falsos negativos reales — "umbral de 8", "apruébalo si llega a 8.0",
// "mínimo 8 para enviar" NO se detectaban por (1) keyword fija ('threshold'),
// (2) ventana de sólo 12 chars entre keyword y número, y (3) sólo mirar números
// DESPUÉS del keyword. Aquí atacamos las tres, priorizando NO mentirle al user:
//   1) `numbersNear` — capa PRECISA: sinónimos es/en por campo + ventana ~28
//      chars + número a cualquier lado del keyword. Cuando dispara, sabemos a qué
//      columna se refiere el número, así que el aviso puede ser específico
//      ("umbral: efectivo X, mencionado Y").
//   2) `decimalsInProse` — capa GENÉRICA honesta: cualquier decimal 0–10 suelto
//      en la prosa que NO coincida con ningún valor efectivo y que la capa
//      precisa NO haya ya atribuido a un campo. No afirmamos qué contradice —
//      sólo pedimos "verifica que no contradiga la columna". Así reducimos
//      falsos negativos sin inventar una atribución que podría ser falsa.

// Pull every number sitting NEAR (before or after, within ~28 chars) one of
// `keywords` in a free-text addendum (e.g. "Threshold 8.0", "umbral de 8",
// "mínimo 8 para enviar", "8 es el piso", "regenera hasta 3 veces"). Used to
// warn when the addendum prose contradicts the effective column value Carlos
// actually enforces. `keywords` is spliced into an alternation, so each entry
// must already be a safe regex fragment.
const NEAR = 28 // chars of tolerance between keyword and number (was 12)
function numbersNear(addendum: string, keywords: string[]): number[] {
  const out: number[] = []
  const alt = keywords.join('|')
  // number-after-keyword  OR  number-before-keyword — the char class between
  // them excludes digits so we don't span across an unrelated number.
  const re = new RegExp(
    `(?:${alt})[^0-9]{0,${NEAR}}(\\d+(?:[.,]\\d+)?)` +
      `|(\\d+(?:[.,]\\d+)?)[^0-9]{0,${NEAR}}(?:${alt})`,
    'gi',
  )
  let m: RegExpExecArray | null
  while ((m = re.exec(addendum)) !== null) {
    const raw = m[1] ?? m[2]
    const n = Number(raw.replace(',', '.'))
    if (!Number.isNaN(n)) out.push(n)
  }
  return out
}

// Every decimal/integer 0–10 (inclusive) mentioned anywhere in the prose. Feeds
// the generic catch-all. Bounded to 0–10 because that's the domain of every
// effective value (threshold/min 0–10, attempts 1–10) — a "$25", "2024" or
// "15%" in the addendum is not a rubric number and must not raise a false alarm.
function decimalsInProse(addendum: string): number[] {
  const out: number[] = []
  const re = /\d+(?:[.,]\d+)?/g
  let m: RegExpExecArray | null
  while ((m = re.exec(addendum)) !== null) {
    const n = Number(m[0].replace(',', '.'))
    if (!Number.isNaN(n) && n >= 0 && n <= 10) out.push(n)
  }
  return out
}

// Keyword sets per field (es-ES + en, tolerant). These feed numbersNear().
// Widened past the original {threshold, umbral} to cover the miss cases the
// drift detector overlooked: "mínimo N para enviar", "apruébalo si llega a N",
// "score N", "min N".
const THRESHOLD_KEYWORDS = [
  'threshold',
  'umbral',
  'm[íi]n(?:imo)?',
  'min',
  'aprob(?:ar|a[rd]?)',
  'apru[eé]ba[lo]*',
  'aprueba',
  'score',
]
const MIN_KEYWORDS = ['min[- ]?score', 'min[- ]?acceptable', 'm[íi]nimo aceptable', 'piso', 'floor']
const ATTEMPTS_KEYWORDS = ['max[- ]?attempts', 'm[áa]x(?:\\.|imo)?\\s*(?:de\\s*)?reintentos', 'reintent[oa]s?', 'reintenta', 'regenera(?:r|ci[óo]n)?', 'attempts?', 'retries', 'retry']

const STEP_LABELS: Record<string, string> = {
  linkedin_connect: 'Invitación LinkedIn',
  send_email: 'Email',
  email_reply: 'Reply en el mismo hilo',
  linkedin_message: 'DM de LinkedIn',
  task: 'Tarea',
  linkedin_comment: 'Comentario LinkedIn',
}

function stepLabel(stepType: string): string {
  return STEP_LABELS[stepType] || stepType
}

interface EditState {
  threshold: string
  min_acceptable: string
  max_attempts: string
  addendum: string
}

function toEdit(row: StepRubricRow): EditState {
  return {
    threshold: String(row.threshold),
    min_acceptable: String(row.min_acceptable),
    max_attempts: String(row.max_attempts),
    addendum: row.system_prompt_addendum ?? '',
  }
}

function StepRubricRowCard({
  row,
  orgId,
  cadenceId,
  onSaved,
}: {
  row: StepRubricRow
  orgId: string
  cadenceId?: string | null
  onSaved: () => void
}) {
  const [edit, setEdit] = useState<EditState>(() => toEdit(row))
  const [expanded, setExpanded] = useState(false)

  const dirty =
    edit.threshold !== String(row.threshold) ||
    edit.min_acceptable !== String(row.min_acceptable) ||
    edit.max_attempts !== String(row.max_attempts) ||
    edit.addendum !== (row.system_prompt_addendum ?? '')

  // ── AMARILLO (herencia): does this EFFECTIVE row come from the org-global
  // legacy config, or is it already scoped to this cadence? Editing an inherited
  // row silently forks a cadence-scoped copy on save. `undefined` = caller didn't
  // thread provenance → hide the badge rather than guess.
  const inherited = row.cadence_id === null
  const showProvenance = row.cadence_id !== undefined

  // ── ROJO-D (valor efectivo > prosa): compare each effective column value
  // (what Carlos actually enforces) with the current addendum text (live edit, so
  // the warning tracks what the user is typing). Warn only when a mentioned
  // number DIFFERS from the effective one — mentioning the same value is fine.
  // Covers threshold, min-score (piso) and max-attempts (reintentos).
  const driftFields: {
    label: string
    effective: number
    mentioned: number[]
  }[] = []
  const effectiveThreshold = Number(edit.threshold)
  const effectiveMin = Number(edit.min_acceptable)
  const effectiveAttempts = Number(edit.max_attempts)
  // Numbers the PRECISE layer already tied to a specific field — excluded from
  // the generic catch-all below so we don't double-warn about the same digit.
  const attributed = new Set<number>()
  if (!Number.isNaN(effectiveThreshold)) {
    const near = numbersNear(edit.addendum, THRESHOLD_KEYWORDS)
    near.forEach((n) => attributed.add(n))
    const mentioned = near.filter((n) => n !== effectiveThreshold)
    if (mentioned.length > 0) driftFields.push({ label: 'umbral', effective: effectiveThreshold, mentioned })
  }
  if (!Number.isNaN(effectiveMin)) {
    const near = numbersNear(edit.addendum, MIN_KEYWORDS)
    near.forEach((n) => attributed.add(n))
    const mentioned = near.filter((n) => n !== effectiveMin)
    if (mentioned.length > 0) driftFields.push({ label: 'piso (min-score)', effective: effectiveMin, mentioned })
  }
  if (!Number.isNaN(effectiveAttempts)) {
    const near = numbersNear(edit.addendum, ATTEMPTS_KEYWORDS)
    near.forEach((n) => attributed.add(n))
    const mentioned = near.filter((n) => n !== effectiveAttempts)
    if (mentioned.length > 0)
      driftFields.push({ label: 'máx. reintentos', effective: effectiveAttempts, mentioned })
  }
  const hasDrift = driftFields.length > 0

  // ── ROJO-D (capa GENÉRICA honesta): a decimal 0–10 floats in the prose that
  // (a) matches NONE of the three effective values and (b) the precise layer did
  // NOT already attribute to a field. This is the catch-all for keyword-less
  // mentions ("apruébalo si llega a 8.0", "manda si pasa de 7") that would
  // otherwise be false negatives. We deliberately DO NOT claim which column it
  // contradicts — the copy just asks the user to verify — so we never mislead by
  // guessing an attribution. Dedup + sort keeps the aviso stable.
  const effectiveValues = [effectiveThreshold, effectiveMin, effectiveAttempts].filter(
    (n) => !Number.isNaN(n),
  )
  const looseNumbers = Array.from(
    new Set(
      decimalsInProse(edit.addendum).filter(
        (n) => !attributed.has(n) && !effectiveValues.includes(n),
      ),
    ),
  ).sort((a, b) => a - b)
  const hasLooseDrift = looseNumbers.length > 0

  // ── ROJO-D (auto-expand): when any field drifts prose-vs-column, the number the
  // user must fix lives inside the (collapsed-by-default) addendum textarea, so we
  // force it open. Derived (not an effect): the textarea shows when the user opened
  // it OR there's drift. This auto-expands on drift, never auto-collapses a manually
  // opened textarea, and leaves the no-drift case untouched. `expanded` still drives
  // the chevron and the manual toggle. Also opens for the generic loose-number
  // aviso so the number the user must reconcile is visible.
  const showAddendum = expanded || hasDrift || hasLooseDrift

  const save = useMutation({
    mutationFn: async () => {
      const threshold = Number(edit.threshold)
      const minAcceptable = Number(edit.min_acceptable)
      const maxAttempts = Number(edit.max_attempts)

      // Client-side range guard (the edge fn re-validates authoritatively).
      if (Number.isNaN(threshold) || threshold < 0 || threshold > 10) {
        throw new Error('El umbral debe estar entre 0 y 10')
      }
      if (Number.isNaN(minAcceptable) || minAcceptable < 0 || minAcceptable > 10) {
        throw new Error('El piso (min-score) debe estar entre 0 y 10')
      }
      if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || maxAttempts > 10) {
        throw new Error('El max de reintentos debe ser un entero entre 1 y 10')
      }
      if (minAcceptable > threshold) {
        throw new Error('El piso no puede ser mayor que el umbral')
      }
      return updateQaSettings({
        operation: 'rubric',
        orgId,
        cadence_id: cadenceId ?? null,
        step_type: row.step_type,
        day_offset: row.day_offset,
        threshold,
        min_acceptable: minAcceptable,
        max_attempts: maxAttempts,
        system_prompt_addendum: edit.addendum.trim() || null,
        // Pass-through JSONB filters (not editable here). Re-send so forking an
        // inherited rubric preserves them; if the row didn't carry them
        // (null/undefined) send undefined so the backend inherits/defaults.
        rubric_focus: row.rubric_focus ?? undefined,
        rubric_skip: row.rubric_skip ?? undefined,
      })
    },
    onSuccess: () => {
      toast.success(`Rúbrica guardada — ${stepLabel(row.step_type)} · Día ${row.day_offset}`)
      onSaved()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar la rúbrica')
    },
  })

  return (
    <div className="rounded-lg border bg-card">
      {/* Row header */}
      <div className="flex flex-wrap items-center gap-3 p-3 border-b">
        <Badge variant="outline" className="shrink-0 font-mono text-xs">
          Día {row.day_offset}
        </Badge>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{stepLabel(row.step_type)}</p>
          <p className="text-xs text-muted-foreground font-mono">{row.step_type}</p>
        </div>
        {showProvenance && (
          <Badge
            variant={inherited ? 'secondary' : 'outline'}
            className="shrink-0 text-[10px]"
          >
            {inherited ? 'Heredado (org-global)' : 'Propio de esta cadencia'}
          </Badge>
        )}
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Guardar
        </Button>
      </div>

      {/* AMARILLO — inherited rows silently fork on save; tell the user up front. */}
      {showProvenance && inherited && (
        <div className="mx-3 mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-tight text-amber-800 flex items-start gap-1.5 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>Al guardar creas una copia específica de <strong>esta</strong> cadencia; la política org-global compartida (la que otras cadencias usan por defecto) no se modifica.</span>
        </div>
      )}

      {/* ROJO-D — the addendum prose mentions a number (threshold / piso /
          reintentos) that contradicts the effective column value Carlos enforces. */}
      {hasDrift && (
        <div className="mx-3 mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-tight text-amber-800 flex items-start gap-1.5 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          <div className="space-y-0.5">
            <p>La instrucción menciona valores distintos a los efectivos que Carlos usa. Manda la columna; edita el texto para evitar confusión.</p>
            <ul className="list-disc pl-4">
              {driftFields.map((f) => (
                <li key={f.label}>
                  {f.label}: efectivo {f.effective}, mencionado {f.mentioned.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ROJO-D genérico — la prosa suelta un número 0–10 que no atamos a ninguna
          columna (sin keyword) y no coincide con ningún valor efectivo. Aviso
          honesto: no afirmamos qué contradice, sólo pedimos verificar. */}
      {hasLooseDrift && (
        <div className="mx-3 mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-tight text-amber-800 flex items-start gap-1.5 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          <p>
            La instrucción menciona números ({looseNumbers.join(', ')}) — verifica que no
            contradigan la columna efectiva (umbral {effectiveThreshold}, piso {effectiveMin},
            reintentos {effectiveAttempts}).
          </p>
        </div>
      )}

      {/* Number editors */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3">
        <div className="space-y-1">
          <Label className="text-xs">Umbral para enviar</Label>
          <Input
            type="number"
            step="0.1"
            min={0}
            max={10}
            value={edit.threshold}
            onChange={(e) => setEdit((s) => ({ ...s, threshold: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Puntaje mínimo (0–10) para que Carlos apruebe y el mensaje salga.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Piso (min-score)</Label>
          <Input
            type="number"
            step="0.1"
            min={0}
            max={10}
            value={edit.min_acceptable}
            onChange={(e) => setEdit((s) => ({ ...s, min_acceptable: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Al agotar reintentos, si el mejor intento llega a este piso se envía; por debajo, se salta el touch.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Máx. reintentos</Label>
          <Input
            type="number"
            step="1"
            min={1}
            max={10}
            value={edit.max_attempts}
            onChange={(e) => setEdit((s) => ({ ...s, max_attempts: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Cuántas veces Carlos pide regenerar antes de mandar el mejor intento (send-best-attempt).
          </p>
        </div>
      </div>

      {/* Per-step rubric addendum */}
      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {showAddendum ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Rúbrica específica de este paso (instrucción extra para Carlos)
          {(row.system_prompt_addendum ?? '').trim() && !showAddendum && (
            <Badge variant="secondary" className="ml-1 text-[10px]">definida</Badge>
          )}
        </button>
        {showAddendum && (
          <div className="mt-2 space-y-1">
            <Textarea
              value={edit.addendum}
              onChange={(e) => setEdit((s) => ({ ...s, addendum: e.target.value }))}
              placeholder="Ej. HARD GATE — prohibir intros de libro de texto…"
              className="min-h-[120px] text-xs font-mono"
            />
            <p className="text-[11px] text-muted-foreground leading-tight flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              Se inyecta como "STEP-SPECIFIC RUBRIC OVERRIDE" en el prompt de Carlos para este paso.
              Es exactamente la regla que hoy se edita por SQL directo (p. ej. el ban de intros genéricas del Día 9).
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export function StepRubricTable({
  rows,
  orgId,
  cadenceId,
}: {
  rows: StepRubricRow[]
  orgId: string
  cadenceId?: string | null
}) {
  const queryClient = useQueryClient()
  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['qa-step-rubrics', orgId] })
    // Carlos' EFFECTIVE threshold is resolved by cadence-flow-metrics (edge fn),
    // which Steps + the PromptDrawer read from. Saving a rubric here changes that
    // effective value, so we must invalidate it too or Steps/drawer show the stale
    // number until staleTime (60s). Prefix-match invalidates every variant of the
    // key ['cadence-flow-metrics', orgId, cadenceId, daysWindow].
    queryClient.invalidateQueries({ queryKey: ['cadence-flow-metrics'] })
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        No hay rúbricas por paso para esta organización todavía. Se crean automáticamente
        con la cadencia de outreach; si esperabas ver filas aquí, revisa que la cadencia
        esté configurada.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <StepRubricRowCard key={row.id} row={row} orgId={orgId} cadenceId={cadenceId} onSaved={onSaved} />
      ))}
    </div>
  )
}
